"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _react = _interopRequireWildcard(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _react2 = require("@linaria/react");
var _patternLibraryUtils = require("@site/pattern-library-utils");
var _animation = require("../../../animation");
function _getRequireWildcardCache(e) { if ("function" != typeof WeakMap) return null; var r = new WeakMap(), t = new WeakMap(); return (_getRequireWildcardCache = function (e) { return e ? t : r; })(e); }
function _interopRequireWildcard(e, r) { if (!r && e && e.__esModule) return e; if (null === e || "object" != typeof e && "function" != typeof e) return { default: e }; var t = _getRequireWildcardCache(r); if (t && t.has(e)) return t.get(e); var n = { __proto__: null }, a = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var u in e) if ("default" !== u && {}.hasOwnProperty.call(e, u)) { var i = a ? Object.getOwnPropertyDescriptor(e, u) : null; i && (i.get || i.set) ? Object.defineProperty(n, u, i) : n[u] = e[u]; } return n.default = e, t && t.set(e, n), n; }
const HeadlineContainer = /*#__PURE__*/(0, _react2.styled)("span")({
  name: "HeadlineContainer",
  class: "pl-hsg3uop",
  vars: {
    "hsg3uop-0": [({
      height
    }) => height ? `${height}px` : '100%']
  }
});
const HeadlineWrapper = /*#__PURE__*/(0, _react2.styled)("span")({
  name: "HeadlineWrapper",
  class: "pl-h8corx5",
  vars: {
    "h8corx5-0": [({
      hasSingleWord
    }) => hasSingleWord ? 'visible' : 'hidden']
  }
});
const LoopHeadline = /*#__PURE__*/(0, _react2.styled)("span")({
  name: "LoopHeadline",
  class: "pl-lzmps0i",
  vars: {
    "lzmps0i-0": [({
      color
    }) => color ?? 'initial']
  }
});

/**
 * TextLoop Component.
 *
 * @param {Object} props React props
 * @param {String} props.dataCy Data identifier
 * @param {Array} props.words Array of words that will be looped through
 * @param {string} props.headlineSize Headline size
 * @param {string} props.textColor Text color
 * @param {string} props.videoId Gets unique id for video
 * @returns {React.ReactElement} TextLoop React element
 * @public
 */

// eslint-disable-next-line max-statements
function TextLoop({
  words,
  headlineSize,
  textColor,
  dataCy,
  videoId
}) {
  const [currentIndex, setCurrentIndex] = (0, _react.useState)(0);
  const [nextIndex, setNextIndex] = (0, _react.useState)(1);
  const [displayNext, setDisplayNext] = (0, _react.useState)(false);
  const hasSingleWord = !words[0].includes(' ');
  const [animation, setAnimation] = (0, _react.useState)({
    current: '',
    next: hasSingleWord ? 'single-word-anim-enter' : 'multi-word-anim-enter'
  });
  const [headlineHeight, setHeadlineHeight] = (0, _react.useState)(null);
  const loopHeadlineRef = (0, _react.useRef)(null);
  const SharedClasses = `${headlineSize} ux-text-heading`;
  const isBrowser = typeof window !== 'undefined';
  const [videoTimes, setVideoTimes] = (0, _react.useState)({});
  (0, _react.useEffect)(() => {
    if (!isBrowser) return;
    const updateSize = () => {
      if (loopHeadlineRef.current) {
        setHeadlineHeight(loopHeadlineRef.current.offsetHeight);
      }
    };
    window.addEventListener('resize', updateSize);
    updateSize();
    return () => {
      window.removeEventListener('resize', updateSize);
    };
  }, []);
  (0, _react.useEffect)(() => {
    const videoElement = document.querySelector(`#${videoId}`);
    if (videoElement) {
      const handleTimeUpdate = () => {
        setVideoTimes(prev => ({
          ...prev,
          [videoId]: {
            ...prev[videoId],
            currentTime: Math.floor(videoElement.currentTime)
          }
        }));
      };
      videoElement.addEventListener('timeupdate', handleTimeUpdate);
      return () => {
        videoElement.removeEventListener('timeupdate', handleTimeUpdate);
      };
    }
  }, [videoId]);
  (0, _react.useEffect)(() => {
    let animationTimeout;
    const videoData = videoTimes[videoId];
    if (!videoData) return;
    const {
      currentTime,
      lastTriggerTime = 0
    } = videoData;
    const isLastWord = nextIndex === 0;
    const offsetTime = isLastWord ? 1 : 0;
    const interval = 6;
    if (currentTime > 0 && (currentTime + offsetTime) % interval === 0 && currentTime !== lastTriggerTime) {
      setVideoTimes(prev => ({
        ...prev,
        [videoId]: {
          ...prev[videoId],
          lastTriggerTime: currentTime
        }
      }));
      animationTimeout = setAnimation(prevAnimation => ({
        ...prevAnimation,
        current: hasSingleWord ? 'single-word-anim-exit' : 'multi-word-anim-exit'
      }));
      setDisplayNext(true);
      setTimeout(() => {
        setCurrentIndex(nextIndex);
        setNextIndex((nextIndex + 1) % words?.length);
        setAnimation({
          current: '',
          next: hasSingleWord ? 'single-word-anim-enter' : 'multi-word-anim-enter'
        });
        setDisplayNext(false);
      }, 1000);
    }
    return () => {
      clearTimeout(animationTimeout);
    };
  }, [videoTimes, videoId, nextIndex, words?.length, hasSingleWord]);
  (0, _react.useEffect)(() => {
    let resetTimeout;
    const videoData = videoTimes[videoId];
    if (!videoData) return;
    const {
      currentTime
    } = videoData;
    if (currentTime === 0) {
      resetTimeout = setTimeout(() => {
        setCurrentIndex(0);
        setNextIndex(1);
        setAnimation({
          current: '',
          next: hasSingleWord ? 'single-word-anim-enter' : 'multi-word-anim-enter'
        });
        setDisplayNext(false);
      }, 1000);
    }
    return () => {
      clearTimeout(resetTimeout);
    };
  }, [videoTimes, videoId, hasSingleWord]);
  if (!words) return null;
  return /*#__PURE__*/_react.default.createElement(HeadlineContainer, {
    height: headlineHeight,
    "data-cy": dataCy || 'headline-loop'
  }, /*#__PURE__*/_react.default.createElement(HeadlineWrapper, {
    hasSingleWord: hasSingleWord
  }, /*#__PURE__*/_react.default.createElement(LoopHeadline, {
    ref: loopHeadlineRef,
    className: `${SharedClasses} ${animation.current} ${!hasSingleWord && 'sentences'}`,
    color: textColor
  }, /*#__PURE__*/_react.default.createElement("span", null, words[currentIndex])), displayNext && /*#__PURE__*/_react.default.createElement(LoopHeadline, {
    className: `${SharedClasses} ${animation.next} ${!hasSingleWord && 'sentences'}`,
    color: textColor
  }, /*#__PURE__*/_react.default.createElement("span", null, words[nextIndex]))));
}
var _default = exports.default = TextLoop;
TextLoop.propTypes = {
  words: _propTypes.default.arrayOf(_propTypes.default.string),
  headlineSize: _propTypes.default.string,
  font: _propTypes.default.string,
  textColor: _propTypes.default.string,
  dataCy: _propTypes.default.string,
  videoId: _propTypes.default.string
};